import { getBackendUrl } from '@/utils/apiUrl';
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Calendar, Plus, Clock, X, Trash2, Edit2, MapPin, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const Appointments = ({ user }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    appointment_date: '',
    duration: 60,
    location: '',
    customer_name: '',
    status: 'scheduled'
  });

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const response = await fetch(getBackendUrl() + '/api/appointments', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      toast.error('Fehler beim Laden der Termine');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingAppointment 
        ? `${getBackendUrl()}/api/appointments/${editingAppointment._id}`
        : `${getBackendUrl()}/api/appointments`;
      
      const method = editingAppointment ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingAppointment ? 'Termin aktualisiert' : 'Termin erstellt');
        setShowModal(false);
        setEditingAppointment(null);
        setFormData({ title: '', description: '', appointment_date: '', duration: 60, location: '', customer_name: '', status: 'scheduled' });
        fetchAppointments();
      } else {
        toast.error('Fehler beim Speichern');
      }
    } catch (error) {
      console.error('Error saving appointment:', error);
      toast.error('Verbindungsfehler');
    }
  };

  const handleDelete = async (appointmentId) => {
    if (!window.confirm('Möchten Sie diesen Termin wirklich löschen?')) return;
    
    try {
      const response = await fetch(`${getBackendUrl()}/api/appointments/${appointmentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        toast.success('Termin gelöscht');
        fetchAppointments();
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  const handleEdit = (appointment) => {
    setEditingAppointment(appointment);
    setFormData({
      title: appointment.title,
      description: appointment.description || '',
      appointment_date: appointment.appointment_date,
      duration: appointment.duration || 60,
      location: appointment.location || '',
      customer_name: appointment.customer_name || '',
      status: appointment.status
    });
    setShowModal(true);
  };

  const handleStatusChange = async (appointment, newStatus) => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/appointments/${appointment._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...appointment,
          status: newStatus
        })
      });

      if (response.ok) {
        toast.success('Status aktualisiert');
        fetchAppointments();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return {
      date: date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      time: date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getTodayAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    return appointments.filter(apt => apt.appointment_date.startsWith(today));
  };

  const getUpcomingAppointments = () => {
    const today = new Date().toISOString().split('T')[0];
    return appointments
      .filter(apt => apt.appointment_date > today)
      .sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date))
      .slice(0, 5);
  };

  const statusColors = {
    scheduled: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Geplant' },
    completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Abgeschlossen' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'Abgesagt' }
  };

  const todayAppointments = getTodayAppointments();
  const upcomingAppointments = getUpcomingAppointments();

  return (
    <Layout user={user}>
      <div className="p-4 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Termine</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1">Verwalten Sie Ihre Termine und Besichtigungen</p>
          </div>
          <button
            onClick={() => {
              setEditingAppointment(null);
              setFormData({ title: '', description: '', appointment_date: '', duration: 60, location: '', customer_name: '', status: 'scheduled' });
              setShowModal(true);
            }}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-lg transition-all min-h-[44px] hover:opacity-90"
            style={{backgroundColor: '#0A4D8C'}}
            data-testid="add-appointment-button"
          >
            <Plus className="w-4 h-4" />
            Neuer Termin
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" style={{color: '#0A4D8C'}}></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
            {/* Today's Appointments */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}}>
                <div className="p-4 md:p-6 border-b" style={{borderColor: '#E1E4E8'}}>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">Heutige Termine</h3>
                </div>
                <div className="p-4 md:p-6">
                  {todayAppointments.length === 0 ? (
                    <div className="text-center py-8 md:py-12 text-sm md:text-base text-gray-500">
                      <Calendar className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 text-gray-300" />
                      <p>Keine Termine für heute</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {todayAppointments.map((appointment) => {
                        const { time } = formatDateTime(appointment.appointment_date);
                        const statusColor = statusColors[appointment.status] || statusColors.scheduled;
                        return (
                          <div key={appointment._id} className="p-4 border rounded-lg hover:shadow-md transition-shadow" style={{borderColor: '#E1E4E8'}} data-testid={`appointment-${appointment._id}`}>
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className="text-base font-semibold text-gray-900">{appointment.title}</h4>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                                    {statusColor.label}
                                  </span>
                                </div>
                                {appointment.customer_name && (
                                  <p className="text-sm text-gray-700 mb-1">Kunde: {appointment.customer_name}</p>
                                )}
                                {appointment.description && (
                                  <p className="text-sm text-gray-600 mb-2">{appointment.description}</p>
                                )}
                                <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Clock className="w-4 h-4" />
                                    <span>{time} ({appointment.duration} Min.)</span>
                                  </div>
                                  {appointment.location && (
                                    <div className="flex items-center gap-1">
                                      <MapPin className="w-4 h-4" />
                                      <span>{appointment.location}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2">
                                {appointment.status === 'scheduled' && (
                                  <button
                                    onClick={() => handleStatusChange(appointment, 'completed')}
                                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                    title="Als abgeschlossen markieren"
                                    data-testid={`complete-appointment-${appointment._id}`}
                                  >
                                    <CheckCircle2 className="w-5 h-5" />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleEdit(appointment)}
                                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Bearbeiten"
                                  data-testid={`edit-appointment-${appointment._id}`}
                                >
                                  <Edit2 className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(appointment._id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Löschen"
                                  data-testid={`delete-appointment-${appointment._id}`}
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* All Appointments List */}
              <div className="bg-white rounded-lg shadow-sm border mt-6" style={{borderColor: '#E1E4E8'}}>
                <div className="p-4 md:p-6 border-b" style={{borderColor: '#E1E4E8'}}>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">Alle Termine</h3>
                </div>
                <div className="p-4 md:p-6">
                  {appointments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>Noch keine Termine erstellt</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {appointments
                        .sort((a, b) => new Date(b.appointment_date) - new Date(a.appointment_date))
                        .map((appointment) => {
                          const { date, time } = formatDateTime(appointment.appointment_date);
                          const statusColor = statusColors[appointment.status] || statusColors.scheduled;
                          return (
                            <div key={appointment._id} className="p-4 border rounded-lg hover:shadow-md transition-shadow" style={{borderColor: '#E1E4E8'}}>
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <h4 className="text-base font-semibold text-gray-900">{appointment.title}</h4>
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.text}`}>
                                      {statusColor.label}
                                    </span>
                                  </div>
                                  {appointment.customer_name && (
                                    <p className="text-sm text-gray-700 mb-1">Kunde: {appointment.customer_name}</p>
                                  )}
                                  <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                                    <div className="flex items-center gap-1">
                                      <Calendar className="w-4 h-4" />
                                      <span>{date} um {time}</span>
                                    </div>
                                    {appointment.location && (
                                      <div className="flex items-center gap-1">
                                        <MapPin className="w-4 h-4" />
                                        <span>{appointment.location}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleEdit(appointment)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="Bearbeiten"
                                  >
                                    <Edit2 className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDelete(appointment._id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Löschen"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Upcoming Sidebar */}
            <div>
              <div className="bg-white rounded-lg shadow-sm border" style={{borderColor: '#E1E4E8'}}>
                <div className="p-4 md:p-6 border-b" style={{borderColor: '#E1E4E8'}}>
                  <h3 className="text-base md:text-lg font-semibold text-gray-900">Bevorstehende Termine</h3>
                </div>
                <div className="p-4 md:p-6">
                  {upcomingAppointments.length === 0 ? (
                    <div className="text-center py-8 text-sm text-gray-500">
                      <p>Keine anstehenden Termine</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {upcomingAppointments.map((appointment) => {
                        const { date, time } = formatDateTime(appointment.appointment_date);
                        return (
                          <div key={appointment._id} className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors" style={{borderColor: '#E1E4E8'}} onClick={() => handleEdit(appointment)}>
                            <div className="flex items-start gap-3">
                              <div className="text-xs md:text-sm font-medium whitespace-nowrap" style={{color: '#0A4D8C'}}>{date.split('.').slice(0, 2).join('.')}</div>
                              <div className="flex-1 min-w-0">
                                <div className="text-xs md:text-sm font-medium text-gray-900 truncate">{appointment.title}</div>
                                {appointment.customer_name && (
                                  <div className="text-xs text-gray-500 truncate">{appointment.customer_name}</div>
                                )}
                              </div>
                              <Clock className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <div className="text-xs text-gray-500">{time}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b" style={{borderColor: '#E1E4E8'}}>
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingAppointment ? 'Termin bearbeiten' : 'Neuer Termin'}
                  </h2>
                  <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{borderColor: '#E1E4E8'}}
                    placeholder="z.B. Besichtigung, Abholung, ..."
                    data-testid="appointment-title-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kunde</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{borderColor: '#E1E4E8'}}
                    placeholder="Name des Kunden"
                    data-testid="appointment-customer-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{borderColor: '#E1E4E8'}}
                    placeholder="Zusätzliche Details..."
                    data-testid="appointment-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Datum & Uhrzeit *</label>
                    <input
                      type="datetime-local"
                      required
                      value={formData.appointment_date}
                      onChange={(e) => setFormData({...formData, appointment_date: e.target.value})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{borderColor: '#E1E4E8'}}
                      data-testid="appointment-date-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Dauer (Minuten)</label>
                    <input
                      type="number"
                      value={formData.duration}
                      onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value) || 60})}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      style={{borderColor: '#E1E4E8'}}
                      min="15"
                      step="15"
                      data-testid="appointment-duration-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ort</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{borderColor: '#E1E4E8'}}
                    placeholder="z.B. Werkstatt, Kundenadresse, ..."
                    data-testid="appointment-location-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    style={{borderColor: '#E1E4E8'}}
                    data-testid="appointment-status-select"
                  >
                    <option value="scheduled">Geplant</option>
                    <option value="completed">Abgeschlossen</option>
                    <option value="cancelled">Abgesagt</option>
                  </select>
                </div>
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="flex-1 px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    style={{borderColor: '#E1E4E8'}}
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity"
                    style={{backgroundColor: '#0A4D8C'}}
                    data-testid="save-appointment-button"
                  >
                    {editingAppointment ? 'Aktualisieren' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Appointments;
